import { useMemo } from "react";
import { flaschenVonWein, flaschenVonWinzer, rebsortenNamen, weineZahlVonWinzer } from "../kern/fachlogik.js";
import { passtWahl } from "../kern/filterwahl.js";
import { sortDe } from "../kern/format.js";
import { MehrfachAuswahl } from "../ui/mehrfachauswahl.jsx";
import { useNachwachsen, usePersistentState } from "../ui/hooks.jsx";
import { Begrenzt } from "../ui/begrenzt.jsx";
import { LISTEN_GRENZEN as G } from "../kern/konstanten.js";
import { Btn, JahrgangsLinks, Leer, LoeschenKnopf, Seitenkopf, TextKnopf, TypPunkt, Werkzeugleiste, ZeilenMenue } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";


function WinzerAnsicht({ db, nurBestand, geheZu, oeffneModal, loescheWinzer }) {
  const [suche, setSuche] = usePersistentState("winzer.suche", "");
  /* Mehrfachwahlen: Liste, leer heißt alle (kern/filterwahl.js). */
  const [landFilter, setLandFilter] = usePersistentState("winzer.landFilter", []);
  const [regionFilter, setRegionFilter] = usePersistentState("winzer.regionFilter", []);
  const landVon = (r) => (r ? r.land || "Ohne Land" : "Ohne Land");

  const laender = useMemo(
    () => [...new Set(db.regions.map((r) => r.land || "Ohne Land"))].sort((a, b) => a.localeCompare(b, "de")),
    [db.regions]);

  const q = suche.trim().toLowerCase();
  /* Gemerkt: useNachwachsen beginnt bei jeder neuen Liste von vorn —
     ein frisches Array je Render hielte es auf der ersten Portion. */
  const liste = useMemo(() => [...db.winzer]
    .filter((winzer) => {
      if (nurBestand && !winzer.nurKatalog && flaschenVonWinzer(db, winzer.id) === 0) return false;
      const region = db.regions.find((r) => r.id === winzer.regionId);
      if (!passtWahl(landFilter, landVon(region))) return false;
      if (!passtWahl(regionFilter, winzer.regionId)) return false;
      if (q) {
        const heu = `${winzer.name} ${winzer.ort || ""} ${region ? region.name : ""} ${winzer.beschreibung || ""}`.toLowerCase();
        if (!heu.includes(q)) return false;
      }
      return true;
    })
    .sort(sortDe), [db, nurBestand, landFilter, regionFilter, q]);
  /* In der Sicht „Alle Weine“ sind das schnell einige hundert Winzer —
     wie Keller und Weine: erst 50, beim Scrollen mehr. */
  const { sichtbar: winzerSichtbar, marke, alles, gesamt } = useNachwachsen(liste);

  /* Regionsauswahl folgt den gewählten Ländern */
  const regionenImFilter = [...db.regions]
    .filter((r) => passtWahl(landFilter, landVon(r)))
    .sort(sortDe);
  const filterAktiv = !!(landFilter.length || regionFilter.length);

  /* Ändert sich die Länderwahl, fallen Regionen heraus, die zu keinem
     gewählten Land mehr gehören — sonst stünde „Mosel" gewählt neben
     „Frankreich" und die Liste bliebe leer. Regionen der weiter
     gewählten Länder bleiben stehen; früher wurde die Region bei
     jedem Landwechsel ganz geleert, mit mehreren Ländern hieße das,
     beim Dazunehmen eines Landes die Regionswahl zu verlieren. */
  const waehleLaender = (neu) => {
    setLandFilter(neu);
    setRegionFilter((alt) => alt.filter((id) => passtWahl(neu, landVon(db.regions.find((r) => r.id === id)))));
  };
  const landLabel = (l) => (l === "Ohne Land" ? t("Ohne Land") : landName(l));

  return (
    <>
      <Werkzeugleiste
        suche={suche} setSuche={setSuche} sucheLabel={t("Winzer durchsuchen …")}
        filterAktiv={filterAktiv}
        onReset={() => { setLandFilter([]); setRegionFilter([]); }}
        aktion={<Btn variante="voll" onClick={() => oeffneModal({ typ: "winzer" })}>{t("Winzer anlegen")}</Btn>}
        filter={
          <>
            <MehrfachAuswahl wahl={landFilter} setWahl={waehleLaender} alleText={t("Alle Länder")} beschriftung={t("Nach Land filtern")}
              optionen={laender.map((l) => ({ wert: l, label: landLabel(l) }))} beschrifte={landLabel} />
            <MehrfachAuswahl wahl={regionFilter} setWahl={setRegionFilter} alleText={t("Alle Regionen")} beschriftung={t("Nach Region filtern")}
              optionen={regionenImFilter.map((r) => ({ wert: r.id, label: r.name }))}
              beschrifte={(id) => (db.regions.find((r) => r.id === id) || {}).name || id} />
          </>
        }
      />
      {liste.length === 0 ? (
        <Leer text={t("Keine Winzer gefunden. Lege einen Winzer an oder passe Suche und Filter an.")}
          aktion={<Btn onClick={() => oeffneModal({ typ: "winzer" })}>{t("Winzer anlegen")}</Btn>} />
      ) : (
        <div className="wl-scroll">
          <table className="wl-tabelle">
            <thead><tr><th>{t("Winzer")}</th><th>{t("Region")}</th><th>{t("Ort")}</th><th>{t("Weine")}</th><th>{t("Flaschen")}</th><th className="wl-rechts"> </th></tr></thead>
            <tbody>
              {winzerSichtbar.map((winzer) => {
                const region = db.regions.find((r) => r.id === winzer.regionId);
                return (
                  <tr key={winzer.id}>
                    <td>
                      <button className="wl-zeilentitel" onClick={() => geheZu({ view: "winzerDetail", id: winzer.id })}>{winzer.name}</button>
                      {winzer.beschreibung && <span className="wl-nebenzeile">{winzer.beschreibung}</span>}
                    </td>
                    <td>{region ? <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</TextKnopf> : "—"}</td>
                    <td>{winzer.ort || "—"}</td>
                    <td className="num">{weineZahlVonWinzer(db, winzer.id, nurBestand)}</td>
                    <td className="num">{flaschenVonWinzer(db, winzer.id)}</td>
                    <td className="wl-rechts">
                      <ZeilenMenue beschriftung={`Aktionen für ${winzer.name}`} eintraege={[
                        { label: "Öffnen", onClick: () => geheZu({ view: "winzerDetail", id: winzer.id }) },
                        { label: "Wein anlegen", onClick: () => oeffneModal({ typ: "wein", kontext: { winzerId: winzer.id } }) },
                        { label: "Bearbeiten", onClick: () => oeffneModal({ typ: "winzer", objekt: winzer }) },
                        { label: "Winzer löschen", gefahr: true,
                          disabledGrund: weineZahlVonWinzer(db, winzer.id, false) > 0 ? "Zuerst die Weine dieses Winzers löschen oder umziehen." : null,
                          onClick: () => loescheWinzer(winzer.id) },
                      ]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {liste.length > 0 && !alles && (
        <div className="wl-nachwachsen" ref={marke}>
          {t("{n} von {m} angezeigt — weiter scrollen für mehr", { n: winzerSichtbar.length, m: gesamt })}
        </div>
      )}
    </>
  );
}


function WinzerDetail({ id, db, geheZu, oeffneModal, loescheWinzer, nurBestand = false }) {
  /* Von der Schale, nicht aus db.prefs: In der Sicht „Alle Weine“ ist
     der Bestandsfilter gegenstandslos — Katalogobjekte haben nie
     Flaschen. Direkt aus den Einstellungen gelesen blieben Winzer und
     Regionen dort leer, obwohl Weine da sind. */
  const winzer = db.winzer.find((w) => w.id === id);
  if (!winzer) return <Leer text={t("Dieser Winzer existiert nicht mehr.")} aktion={<Btn onClick={() => geheZu({ view: "winzer" })}>{t("Zur Winzerliste")}</Btn>} />;
  const region = db.regions.find((r) => r.id === winzer.regionId);
  const weine = db.weine.filter((w) => w.winzerId === winzer.id).sort(sortDe);
  /* Anzeige respektiert „Nur mit Bestand"; die Vollliste bleibt für die
     Löschsperre erhalten. */
  const weineSichtbar = nurBestand ? weine.filter((w) => flaschenVonWein(w) > 0) : weine;
  const flaschen = flaschenVonWinzer(db, winzer.id);

  return (
    <>
      <Seitenkopf
        krume={<><TextKnopf onClick={() => geheZu({ view: "winzer" })}>{t("Winzer")}</TextKnopf><span> / {winzer.name}</span></>}
        titel={winzer.name}
        unterzeile={
          <>
            {region && <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</TextKnopf>}
            {winzer.ort && <> · {winzer.ort}</>}
          </>
        }
        aktionen={
          <>
            {flaschen > 0 && (
              <Btn onClick={() => geheZu({ view: "keller", filter: { winzerId: winzer.id } })}>{t("Im Keller zeigen")}</Btn>
            )}
            <Btn onClick={() => oeffneModal({ typ: "winzer", objekt: winzer })}>{t("Bearbeiten")}</Btn>
            <LoeschenKnopf
              label={t("Winzer löschen")}
              disabledGrund={weine.length > 0 ? "Zuerst die Weine dieses Winzers löschen oder umziehen." : null}
              onDelete={() => { loescheWinzer(winzer.id); geheZu({ view: "winzer" }); }}
            />
          </>
        }
      />

      {winzer.beschreibung && <p className="wl-fliesstext">{winzer.beschreibung}</p>}

      <section className="wl-abschnitt">
        <div className="wl-abschnittskopf">
          <h2 className="wl-abschnittstitel">{t("Weine")}</h2>
          <Btn variante="voll" onClick={() => oeffneModal({ typ: "wein", kontext: { winzerId: winzer.id } })}>{t("Wein anlegen")}</Btn>
        </div>
        {weine.length === 0 ? (
          <Leer text={t("Für diesen Winzer ist noch kein Wein erfasst.")} />
        ) : weineSichtbar.length === 0 ? (
          <Leer text={t("Kein Wein dieses Winzers hat aktuell Bestand. Deaktiviere „Nur mit Bestand“, um alle zu sehen.")} />
        ) : (
          <Begrenzt liste={weineSichtbar} grenze={G.winzerWeine} schluessel={`${winzer.id}|${nurBestand}`}>{(zeilen) => (
          <div className="wl-scroll">
            <table className="wl-tabelle">
              <thead><tr><th>{t("Wein")}</th><th>{t("Typ")}</th><th>{t("Rebsorten")}</th><th>{t("Jahrgänge")}</th><th className="num">{t("Flaschen")}</th></tr></thead>
              <tbody>
                {zeilen.map((wein) => (
                  <tr key={wein.id}>
                    <td><button className="wl-zeilentitel" onClick={() => geheZu({ view: "wein", id: wein.id })}>{wein.name}{wein.favorit && <span className="wl-favorit" title={t("Lieblingswein")}>★</span>}</button></td>
                    <td><TypPunkt typ={wein.typ} /></td>
                    <td>{rebsortenNamen(wein) || "—"}</td>
                    <td><JahrgangsLinks wein={wein} nurBestand={nurBestand} geheZu={geheZu} /></td>
                    <td className="num">{flaschenVonWein(wein)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}</Begrenzt>
        )}
      </section>
    </>
  );
}

export { WinzerAnsicht, WinzerDetail };
